import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import ElementChart from './ElementChart';
import QuestionDetails from './QuestionDetails';
import HappinessIndex from './HappinessIndex';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLayout, FiTarget, FiClock } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Data State
  const [activeElements, setActiveElements] = useState([]);
  const [clusteredElements, setClusteredElements] = useState([]); // Array of { ...cluster, elements: [] }
  const [allQuestions, setAllQuestions] = useState({}); // Map: elementId -> [questions]
  const [questionScores, setQuestionScores] = useState({}); // Map: questionId -> score
  const [elementScores, setElementScores] = useState({}); // Map: elementId -> score
  const [userElementScores, setUserElementScores] = useState({}); // Map: elementId -> userScore
  
  // UI State
  const [selectedElement, setSelectedElement] = useState(null);
  const [activeClusterId, setActiveClusterId] = useState(null); // State für aktiven Tab
  const [showUserScores, setShowUserScores] = useState(false); // Toggle State for User Scores

  useEffect(() => {
    if (user?.teamId) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // 1. Initial Fetch
      const [
        teamMembersResponse, 
        teamUsesElementResponse, 
        clustersResponse,
        clusterRelationsResponse
      ] = await Promise.all([
        apiService.getUserTeamRelations({ team_id: user.teamId, limit: 1000 }),
        apiService.getTeamUsesElement({ team_id: user.teamId, is_used: 1, limit: 1000 }),
        apiService.getClusters(),
        apiService.getClusterElementRelations()
      ]);

      // --- Process Members ---
      const members = teamMembersResponse.data 
        ? teamMembersResponse.data.map(m => m.user_id) 
        : [];
      if (user.id && !members.includes(parseInt(user.id))) {
        members.push(parseInt(user.id));
      }

      // --- Process Active Elements ---
      if (!teamUsesElementResponse.data || teamUsesElementResponse.data.length === 0) {
        setLoading(false);
        setActiveElements([]);
        setClusteredElements([]);
        return;
      }

      const activeElementIds = teamUsesElementResponse.data.map(r => r.element_id);

      // 2. Dependent Fetch
      const promises = [
        activeElementIds.length > 0 
          ? apiService.getElements({ 'id[in]': activeElementIds.join(',') })
          : Promise.resolve({ data: [] }),
        activeElementIds.length > 0 
          ? apiService.getQuestions({ 'element_id[in]': activeElementIds.join(','), limit: 1000 })
          : Promise.resolve({ data: [] }),
        members.length > 0
          ? apiService.getEvaluations({ 'user_id[in]': members.join(','), limit: 2000 })
          : Promise.resolve({ data: [] })
      ];

      const [elementsResponse, questionsResponse, evaluationsResponse] = await Promise.all(promises);

      // --- Process Elements ---
      const validElements = elementsResponse.data || [];
      setActiveElements(validElements);

      // --- Process Clusters & Grouping ---
      const clusters = clustersResponse.data || [];
      const clusterRelations = clusterRelationsResponse.data || [];
      
      const clustersMap = {};
      clusters.forEach(c => {
         clustersMap[c.id] = { ...c, elements: [] };
      });
      
      const noClusterElements = [];
      validElements.forEach(el => {
         const rel = clusterRelations.find(r => r.element_id === el.id);
         if (rel && clustersMap[rel.cluster_id]) {
            clustersMap[rel.cluster_id].elements.push(el);
         } else {
            noClusterElements.push(el);
         }
      });
      
      const groupedData = Object.values(clustersMap).filter(c => c.elements.length > 0);
      if (noClusterElements.length > 0) {
         groupedData.push({ 
           id: 'none', 
           title: 'Allgemein / Ohne Cluster', 
           description: 'Elemente ohne spezifische Cluster-Zuordnung',
           elements: noClusterElements 
         });
      }
      
      setClusteredElements(groupedData);
      
      if (groupedData.length > 0) {
        setActiveClusterId(groupedData[0].id);
        if (groupedData[0].elements && groupedData[0].elements.length > 0) {
          setSelectedElement(groupedData[0].elements[0]);
        }
      }

      // --- Process Questions ---
      const questionsMap = {};
      const relevantQuestionIds = new Set();
      const fetchedQuestions = questionsResponse.data || [];
      
      fetchedQuestions.forEach(q => {
        if (activeElementIds.includes(q.element_id)) {
          if (!questionsMap[q.element_id]) {
            questionsMap[q.element_id] = [];
          }
          questionsMap[q.element_id].push(q);
          relevantQuestionIds.add(q.id);
        }
      });
      setAllQuestions(questionsMap);

      // --- Process Scores ---
      const allTeamEvaluations = evaluationsResponse.data || [];
      
      // Filter for latest per user per question
      const latestEvaluationsMap = new Map();
      allTeamEvaluations.forEach(ev => {
          const key = `${ev.user_id}-${ev.question_id}`;
          const existing = latestEvaluationsMap.get(key);
          
          const evDate = ev.evaluation_date || '0000-00-00';
          const existingDate = existing?.evaluation_date || '0000-00-00';
          
          // Keep if no existing found, or this one is strictly newer, or same date but higher ID
          if (!existing || evDate > existingDate || (evDate === existingDate && ev.id > existing.id)) {
              latestEvaluationsMap.set(key, ev);
          }
      });
      
      const relevantEvaluations = Array.from(latestEvaluationsMap.values())
          .filter(ev => relevantQuestionIds.has(ev.question_id));

      const qScores = {};
      const eScores = {};
      const uScores = {}; // User Scores

      // Separate User Evaluations
      const userEvaluations = relevantEvaluations.filter(ev => ev.user_id === parseInt(user.id));

      relevantQuestionIds.forEach(qId => {
        const questionEvals = relevantEvaluations.filter(ev => ev.question_id === qId);
        if (questionEvals.length > 0) {
          const sum = questionEvals.reduce((acc, curr) => acc + curr.evaluation_value, 0);
          qScores[qId] = Math.round((sum / questionEvals.length) * 10) / 10;
        } else {
          qScores[qId] = 0;
        }
      });
      setQuestionScores(qScores);

      validElements.forEach(el => {
        const elQuestions = questionsMap[el.id] || [];
        
        // Team Score Calculation
        if (elQuestions.length > 0) {
          const elEvaluations = relevantEvaluations.filter(ev => 
             elQuestions.some(q => q.id === ev.question_id)
          );

          if (elEvaluations.length > 0) {
            const sum = elEvaluations.reduce((acc, curr) => acc + curr.evaluation_value, 0);
            eScores[el.id] = Math.round((sum / elEvaluations.length) * 10) / 10;
          } else {
            eScores[el.id] = 0;
          }

          // User Score Calculation
          const elUserEvals = userEvaluations.filter(ev => 
            elQuestions.some(q => q.id === ev.question_id)
          );

          if (elUserEvals.length > 0) {
            const sum = elUserEvals.reduce((acc, curr) => acc + curr.evaluation_value, 0);
            uScores[el.id] = Math.round((sum / elUserEvals.length) * 10) / 10;
          } else {
            uScores[el.id] = 0;
          }

        } else {
          eScores[el.id] = 0;
          uScores[el.id] = 0;
        }
      });
      setElementScores(eScores);
      setUserElementScores(uScores);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleElementClick = (element) => {
    setSelectedElement(element);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden flex flex-col bg-gray-50">
      <div className="mb-4 flex-shrink-0 flex justify-between items-end">
        <div>
          <h1 className="text-xl font-bold text-brand-dark flex items-center">
            <SafeIcon icon={FiLayout} className="w-6 h-6 mr-2 text-brand-secondary" />
            Team Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Übersicht der Team-Bewertungen nach Clustern.
          </p>
        </div>
      </div>

      <div className="flex-grow overflow-hidden flex flex-col space-y-4">
        {/* Happiness Index Section */}
        <div className="flex-shrink-0">
          <HappinessIndex />
        </div>

        {activeElements.length === 0 ? (
          <div className="flex-grow overflow-y-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTarget} className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-brand-dark mb-2">
                Keine aktiven Elemente
              </h3>
              <p className="text-gray-500 max-w-md mx-auto text-sm">
                Es sind momentan keine Elemente für Ihr Team aktiviert. Der Team-Owner muss zuerst Module in der Team-Verwaltung freischalten.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow flex flex-col flex-grow overflow-hidden border border-gray-200">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 flex-shrink-0 bg-gray-50/50 flex justify-between items-center pr-4">
              <nav className="flex flex-wrap -mb-px px-2" aria-label="Tabs">
                {clusteredElements.map(cluster => (
                  <button
                    key={cluster.id}
                    onClick={() => {
                        setActiveClusterId(cluster.id);
                        const firstElement = cluster.elements && cluster.elements.length > 0 ? cluster.elements[0] : null;
                        if (!selectedElement || !cluster.elements.find(e => e.id === selectedElement.id)) {
                            setSelectedElement(firstElement);
                        }
                    }}
                    className={`
                      group inline-flex items-center py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                      ${activeClusterId === cluster.id
                        ? 'border-brand-secondary text-brand-secondary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    `}
                  >
                    {cluster.title}
                  </button>
                ))}
              </nav>
              
              <Link 
                 to="/element-history"
                 className="text-xs text-brand-secondary hover:text-brand-dark flex items-center font-medium transition-colors"
               >
                 <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                 Verlauf anzeigen
               </Link>
            </div>

            <div className="flex-grow overflow-hidden p-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 h-full divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                
                {/* Left Column: Active Chart */}
                <div className="overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent h-full flex items-start justify-center">
                  
                  {/* Active Cluster Content */}
                  {clusteredElements.map((cluster) => {
                    if (cluster.id !== activeClusterId) return null;
                    
                    return (
                      <div key={cluster.id} className="flex flex-col animate-fadeIn w-full">
                        <div className="flex-shrink-0 mb-2 flex justify-between items-start">
                          <div>
                            <h2 className="text-base font-semibold text-brand-dark flex items-center">
                              {cluster.title}
                            </h2>
                            {cluster.description && (
                               <p className="text-xs text-gray-500 mt-0.5">{cluster.description}</p>
                            )}
                          </div>
                          
                          {/* Toggle for User Scores */}
                          <div className="flex items-center ml-4">
                            <span className="text-xs text-gray-500 mr-2 font-medium">Meine Bewertung</span>
                            <button
                                onClick={() => setShowUserScores(!showUserScores)}
                                className={`
                                    relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:ring-offset-2
                                    ${showUserScores ? 'bg-brand-secondary' : 'bg-gray-200'}
                                `}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`
                                        pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                        ${showUserScores ? 'translate-x-4' : 'translate-x-0'}
                                    `}
                                />
                            </button>
                          </div>
                        </div>
                        
                        <div 
                          className="flex items-start justify-center w-full mt-4"
                          data-tour="dashboard-radar-chart"
                        >
                          <ElementChart 
                            elements={cluster.elements} 
                            scores={elementScores}
                            userScores={userElementScores}
                            showUserScores={showUserScores}
                            onElementClick={handleElementClick}
                            selectedElement={selectedElement}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right Column: Fixed Detail View */}
                <div 
                  className="h-full flex flex-col overflow-hidden bg-white"
                  data-tour="dashboard-detail-view"
                >
                  <div className="p-4 border-b border-gray-100 flex-shrink-0 bg-gray-50/30">
                    <h2 className="text-base font-semibold text-brand-dark">
                      {selectedElement ? (
                        <span className="flex items-center text-brand-secondary">
                          <SafeIcon icon={FiTarget} className="w-4 h-4 mr-2" />
                          {selectedElement.title}
                        </span>
                      ) : 'Detailansicht'}
                    </h2>
                  </div>
                  
                  <div className="flex-grow overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    {selectedElement ? (
                      <QuestionDetails 
                        element={selectedElement}
                        questions={allQuestions[selectedElement.id]}
                        scores={questionScores}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <SafeIcon icon={FiTarget} className="w-12 h-12 mb-3 opacity-20" />
                        <p className="text-center text-sm">Wählen Sie ein Element im Netzdiagramm aus,<br/>um detaillierte Fragen-Auswertungen zu sehen.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;